// Who else is on the page and where: a name, a colour, the node they have
// selected and their cursor in its text. Cursors are Loro's stable cursors,
// so a caret stays on its character while others type before it.
// Presence is never stored; it goes over the same transport as edits.
// meets: together/presence
import { Cursor, EphemeralStore, type LoroDoc } from "loro-crdt";
import { PRESENCE, type Sync } from "./sync";
import type { Page, TreeID } from "./doc";

export type Peer = {
  name: string;
  hue: number;
  node?: TreeID; // the node they have selected, or are typing in
  anchor?: number[]; // encoded cursors into that node's text
  head?: number[];
  at: number;
};

const NAMES = ["Fox", "Owl", "Hare", "Wren", "Seal", "Lynx", "Moth", "Newt", "Crow", "Mole", "Yak", "Ibis"];

export class Presence {
  readonly me: string;
  readonly store = new EphemeralStore<Record<string, Peer>>(15_000);
  private self: Peer;
  private timer: ReturnType<typeof setInterval>;
  private seen = new Set<string>();
  onChange?: () => void;

  constructor(private page: Page, private sync: Sync) {
    this.me = page.doc.peerIdStr;
    const pick = Number(BigInt(this.me) % BigInt(NAMES.length));
    this.self = { name: NAMES[pick], hue: Number(BigInt(this.me) % 360n), at: Date.now() };
    this.store.subscribeLocalUpdates((b) => sync.send(PRESENCE, b));
    sync.on(PRESENCE, (b) => {
      this.store.apply(b);
      // Someone new: tell them about us now, not at the next heartbeat.
      const fresh = this.store.keys().some((k) => k !== this.me && !this.seen.has(k));
      this.store.keys().forEach((k) => this.seen.add(k));
      if (fresh) this.publish();
      this.onChange?.();
    });
    this.publish();
    this.timer = setInterval(() => this.publish(), 5000);
    addEventListener("pagehide", () => this.store.delete(this.me));
  }

  get name() {
    return this.self.name;
  }
  get hue() {
    return this.self.hue;
  }

  // Where this tab is: a node, and a range in its text if it has one.
  set(node: TreeID | undefined, from?: number, to?: number) {
    const text = node ? this.page.text(node) : undefined;
    const enc = (pos?: number) => (text && pos !== undefined ? Array.from(text.getCursor(pos)?.encode() ?? []) : undefined);
    this.self = { ...this.self, node, anchor: enc(from), head: enc(to) };
    this.publish();
  }

  // Someone this tab speaks for, such as the AI while it works: shown to
  // everyone like a person, until set to undefined.
  speakFor(peer: string, p: Omit<Peer, "at"> | undefined) {
    if (p) this.store.set(peer, { ...p, at: Date.now() });
    else this.store.delete(peer);
  }

  private publish() {
    this.self = { ...this.self, at: Date.now() };
    this.store.set(this.me, this.self);
  }

  // Everyone else, with their cursor positions as they are now.
  others(doc: LoroDoc): Array<Peer & { peer: string; from?: number; to?: number }> {
    const all = this.store.getAllStates() as Record<string, Peer>;
    const pos = (c?: number[]) => {
      if (!c?.length) return undefined;
      try {
        return doc.getCursorPos(Cursor.decode(new Uint8Array(c)))?.offset;
      } catch {
        return undefined;
      }
    };
    return Object.entries(all)
      .filter(([k, p]) => k !== this.me && p)
      .map(([peer, p]) => ({ ...p, peer, from: pos(p.anchor), to: pos(p.head) }));
  }

  close() {
    clearInterval(this.timer);
    this.store.delete(this.me);
  }
}
